import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./OrderPages.css";
import useApiBase from "./api/useApiBase";
import AuthGate from "./auth/AuthGate";
import useAuthSession from "./auth/useAuthSession";
import AutoRefreshBar from "./AutoRefreshBar";
import { useSyncedOrderPagesAutoRefresh } from "./orderPagesAutoRefresh";
import usePageTitle from "./usePageTitle";

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatBytes = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return "N/A";
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / 1024 / 1024).toFixed(2)} MB`;
};

function WorkerLineDetailPage({ lineNo }) {
  usePageTitle(lineNo ? `Line ${lineNo} – Orders` : "Worker Line");
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useSyncedOrderPagesAutoRefresh();
  const apiBase = useApiBase();
  const auth = useAuthSession(apiBase);
  const isMountedRef = useRef(true);

  const fetchIntervalMs = useMemo(() => {
    const env = process.env.REACT_APP_FETCH_INTERVAL_MS;
    const parsed = Number(env);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 60000;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!lineNo) return;
    try {
      const response = await auth.fetchWithAuth(
        `/worker_lines/${encodeURIComponent(lineNo)}/`
      );
      if (response.status === 404) {
        if (isMountedRef.current) {
          setOrders([]);
          setError("No logs for this line.");
        }
        return;
      }
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      const payload = await response.json();
      if (isMountedRef.current) {
        setOrders(Array.isArray(payload?.orders) ? payload.orders : []);
        setError("");
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : "Unknown error");
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [auth.fetchWithAuth, lineNo]);

  useEffect(() => {
    if (!auth.authChecked || !auth.isAuthenticated) return;
    if (!lineNo) {
      setLoading(false);
      setError("Missing line_no.");
      return;
    }
    let intervalId = null;
    fetchOrders();
    if (autoRefresh) intervalId = setInterval(fetchOrders, fetchIntervalMs);
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [auth.authChecked, auth.isAuthenticated, autoRefresh, fetchIntervalMs, fetchOrders, lineNo]);

  return (
    <AuthGate auth={auth}>
      <div className="order-page">
        <div className="order-container">
          <a className="order-back-link" href="/order_logs">
            Back to worker lines
          </a>
          <h1 className="order-title">worker_line_{lineNo} — orders</h1>
          <AutoRefreshBar
            autoRefresh={autoRefresh}
            onAutoRefreshChange={setAutoRefresh}
            fetchIntervalMs={fetchIntervalMs}
            onRefreshNow={fetchOrders}
            subtitleWhenOn={`Lista odświeża się co ${Math.round(fetchIntervalMs / 1000)} s`}
            subtitleWhenOff="Odświeżanie co interwał jest wyłączone"
          />
          {error && <div className="order-error">{error}</div>}
          {loading ? (
            <div className="order-loader">
              <div className="order-spinner" />
              <p>Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div>No orders.</div>
          ) : (
            <table className="order-table">
              <thead>
                <tr>
                  <th>Production No</th>
                  <th>Log lines</th>
                  <th>Size</th>
                  <th>Newest log</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.production_no}>
                    <td>
                      <a
                        href={`/order_logs/${encodeURIComponent(lineNo)}/${encodeURIComponent(order.production_no)}`}
                        className="order-link"
                      >
                        {order.production_no}
                      </a>
                    </td>
                    <td>{order.log_lines}</td>
                    <td>{formatBytes(order.size_bytes)}</td>
                    <td>{formatDateTime(order.newest_log)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AuthGate>
  );
}

export default WorkerLineDetailPage;
